/**
 * Authorized Cognitory resolution + selection state for the landing shell.
 * Does not cache authorization across sessions; re-fetches on mount/sign-in.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchAuthorizedCognitories,
  type AuthorizedCognitoryEntry,
} from "../api/cognitoryAccessClient";
import { isValidCognitoryWorkspaceId } from "../presentation/cognitoryWorkspaceNavigation";

export type CognitoryAccessPhase =
  | "idle_signed_out"
  | "resolving"
  | "one"
  | "multiple"
  | "none"
  | "unable_to_verify"
  | "temporary_failure"
  | "unauthenticated";

export type CognitoryAccessState = {
  phase: CognitoryAccessPhase;
  cognitories: AuthorizedCognitoryEntry[];
  selected: AuthorizedCognitoryEntry | null;
  requestId: string | null;
  selectCognitory: (cognitoryId: string) => void;
  clearSelection: () => void;
  retry: () => void;
};

export function selectAuthorizedCognitoryIfPermitted(input: {
  cognitoryId: string;
  authorizedCognitories: AuthorizedCognitoryEntry[];
}): AuthorizedCognitoryEntry | null {
  if (!isValidCognitoryWorkspaceId(input.cognitoryId)) {
    return null;
  }
  return (
    input.authorizedCognitories.find(
      entry => entry.cognitoryId === input.cognitoryId,
    ) ?? null
  );
}

function resolveAccessPhase(input: {
  cognitories: AuthorizedCognitoryEntry[];
  selected: AuthorizedCognitoryEntry | null;
}): CognitoryAccessPhase {
  if (input.cognitories.length === 0) {
    return "none";
  }
  if (input.selected) {
    return input.cognitories.length === 1 ? "one" : "multiple";
  }
  if (input.cognitories.length === 1) {
    return "one";
  }
  return "multiple";
}

export function useAuthorizedCognitoryAccess(input: {
  signedIn: boolean;
  requestedCognitoryId?: string | null;
}): CognitoryAccessState {
  const [phase, setPhase] = useState<CognitoryAccessPhase>(
    input.signedIn ? "resolving" : "idle_signed_out",
  );
  const [cognitories, setCognitories] = useState<AuthorizedCognitoryEntry[]>(
    [],
  );
  const [selected, setSelected] = useState<AuthorizedCognitoryEntry | null>(
    null,
  );
  const [requestId, setRequestId] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const authorizedIdsRef = useRef<Set<string>>(new Set());
  const cognitoriesRef = useRef<AuthorizedCognitoryEntry[]>([]);

  useEffect(() => {
    if (!input.signedIn) {
      setPhase("idle_signed_out");
      setCognitories([]);
      setSelected(null);
      setRequestId(null);
      authorizedIdsRef.current = new Set();
      cognitoriesRef.current = [];
      return;
    }

    let cancelled = false;
    setPhase("resolving");
    setCognitories([]);
    setSelected(null);
    setRequestId(null);
    authorizedIdsRef.current = new Set();
    cognitoriesRef.current = [];

    void (async () => {
      const result = await fetchAuthorizedCognitories();
      if (cancelled) return;

      setRequestId(result.requestId);
      if (result.kind !== "resolved") {
        setCognitories([]);
        setSelected(null);
        authorizedIdsRef.current = new Set();
        cognitoriesRef.current = [];
        if (result.kind === "unauthenticated") {
          setPhase("unauthenticated");
        } else if (result.kind === "temporary_failure") {
          setPhase("temporary_failure");
        } else {
          setPhase("unable_to_verify");
        }
        return;
      }

      const list = result.cognitories;
      setCognitories(list);
      cognitoriesRef.current = list;
      authorizedIdsRef.current = new Set(list.map(entry => entry.cognitoryId));

      const requestedSelection =
        input.requestedCognitoryId &&
        selectAuthorizedCognitoryIfPermitted({
          cognitoryId: input.requestedCognitoryId,
          authorizedCognitories: list,
        });

      if (requestedSelection) {
        setSelected(requestedSelection);
        setPhase(
          resolveAccessPhase({
            cognitories: list,
            selected: requestedSelection,
          }),
        );
        return;
      }

      if (list.length === 0) {
        setSelected(null);
        setPhase("none");
        return;
      }
      if (list.length === 1) {
        setSelected(list[0]!);
        setPhase("one");
        return;
      }
      setSelected(null);
      setPhase("multiple");
    })();

    return () => {
      cancelled = true;
    };
  }, [input.signedIn, input.requestedCognitoryId, reloadToken]);

  const selectCognitory = useCallback((cognitoryId: string) => {
    if (!authorizedIdsRef.current.has(cognitoryId)) {
      return;
    }
    const match = selectAuthorizedCognitoryIfPermitted({
      cognitoryId,
      authorizedCognitories: cognitoriesRef.current,
    });
    if (match) {
      setSelected(match);
    }
  }, []);

  const clearSelection = useCallback(() => {
    setSelected(null);
    if (cognitories.length > 1) {
      setPhase("multiple");
    }
  }, [cognitories.length]);

  const retry = useCallback(() => {
    setReloadToken(value => value + 1);
  }, []);

  return {
    phase,
    cognitories,
    selected,
    requestId,
    selectCognitory,
    clearSelection,
    retry,
  };
}
